/**
 * Error Boundary Component
 * React 컴포넌트 트리에서 발생하는 에러를 캐치하고 처리
 */
import React from 'react'
import { FaExclamationTriangle, FaHome, FaRedo } from 'react-icons/fa'

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    }
  }

  static getDerivedStateFromError(error) {
    // 다음 렌더에서 fallback UI가 보이도록 state를 업데이트
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    // 브라우저 확장 프로그램 오류는 무시
    const errorString = JSON.stringify({ error, errorInfo })
    const isExtensionError = /extension:\/\/|chrome-extension:\/\/|moz-extension:\/\/|solanaActionsContentScript|content\.js|Cannot use 'in' operator to search for 'animation' in undefined|createLucideIcon|web-helper\.ts-loader/i.test(errorString)
    
    if (isExtensionError) {
      console.warn('Extension error caught by ErrorBoundary (ignored):', error)
      return
    }
    
    // 에러 리포팅 서비스에 에러를 기록
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    
    this.setState({
      error,
      errorInfo
    })

    // 에러 추적 서비스에 전송 (예: Sentry)
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: false
      })
    }
  }

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null,
      errorInfo: null 
    })
  }

  render() {
    if (this.state.hasError) {
      // 커스텀 fallback UI
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center px-4">
          <div className="max-w-2xl w-full bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 text-center">
            <FaExclamationTriangle className="text-yellow-400 text-6xl mx-auto mb-6" />
            <h1 className="text-4xl font-bold text-white mb-4">
              문제가 발생했습니다
            </h1>
            <p className="text-blue-200 text-lg mb-6">
              죄송합니다. 예상치 못한 오류가 발생했습니다. 페이지를 새로고침하거나 홈으로 돌아가주세요.
            </p>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6 text-left">
                <p className="text-red-300 font-mono text-sm mb-2">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <details className="text-red-200 text-xs">
                    <summary className="cursor-pointer mb-2">스택 트레이스</summary>
                    <pre className="overflow-auto max-h-40">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <div className="flex gap-4 justify-center">
              <button
                onClick={this.handleReset}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors duration-300 flex items-center gap-2"
              >
                <FaRedo />
                다시 시도
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors duration-300 flex items-center gap-2"
              >
                <FaHome />
                홈으로
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary

